/**
 * Adapter: PL `BrandComparisonWidgetData` (proto) -> legacy `ProductComparisonBrand[]` domain objects.
 *
 * The BRAND_COMPARISON widget carries the competitor brand tables (with `additionalProperties`,
 * `freeReturns`, `freeShipping`, and a per-variant `prices[]`). The legacy `ProductPriceComparision`
 * molecule renders each brand from a `ProductComparisonBrand` whose `prices[0]` is the price for the
 * currently selected variant — so this adapter filters each brand's `prices[]` down to the resolved
 * variant (published by the buy box) and drops brands left without a positive price, mirroring
 * `productHelper.getBrandComparison`.
 *
 * Pure (no hooks / no React) so it is unit-testable against the BE JSON.
 */

import type Product from '@src/quince-site-common/src/domains/Product'
import ProductComparisonBrand, {
  ComparisonBrandPrice,
  type PCBAdditionalProperties,
} from '@src/quince-site-common/src/domains/ProductComparisonBrand'

import type {
  BrandComparison as ProtoBrandComparison,
  BrandComparisonAdditionalProperty as ProtoAdditionalProperty,
  BrandComparisonWidgetData,
  TypedValue,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * Collapse the open-typed proto `TypedValue` into the `boolean | number` the legacy domain uses. The BE
 * sends booleans as `stringValue: "true" | "false"` (see the sample payload), so coerce those; numeric
 * strings become numbers (fixed pricing attributes render via `FormattedPrice`); anything else falls
 * back to truthiness.
 */
export function coerceTypedValue(value?: TypedValue): boolean | number {
  if (!value) return false
  if (value.boolValue !== undefined) return value.boolValue
  if (value.intValue !== undefined) return value.intValue
  if (value.doubleValue !== undefined) return value.doubleValue
  return value.stringValue as unknown as number | boolean
}

const mapAdditionalProperties = (props: ProtoAdditionalProperty[] = []): PCBAdditionalProperties[] =>
  props.map(prop => ({
    key: prop.key,
    name: prop.name ?? '',
    value: coerceTypedValue(prop.value),
  }))

/**
 * Build the `ProductComparisonBrand[]` the comparison table renders from.
 *
 * @param data              the BRAND_COMPARISON widget data
 * @param productId         PDP product id keying into `data.products`
 * @param selectedVariantId resolved variant (from the buy box) whose price each brand column shows
 */
export function toProductComparisonBrands(
  data: BrandComparisonWidgetData,
  productId: string,
  selectedVariantId: number | undefined,
): ProductComparisonBrand[] {
  const brandComparisons: ProtoBrandComparison[] = data.products[productId]?.brandComparisons ?? []

  return brandComparisons
    .filter(brand => brand.showOnSite)
    .map(brand => {
      const prices: ComparisonBrandPrice[] = []
      if (selectedVariantId !== undefined) {
        const entry = brand.prices.find(price => price.variantId === selectedVariantId)
        if (entry && entry.price > 0) {
          prices.push(
            new ComparisonBrandPrice({
              variantId: entry.variantId,
              price: entry.price,
              updatedAt: entry.updatedAt ? new Date(entry.updatedAt) : undefined,
              giftCardCredit: entry.giftCardCredit,
            }),
          )
        }
      }
      return toBrand(brand, prices)
    })
    .filter(brand => brand.prices.length > 0)
}

/** One required bundle child's resolved variant, as published by BUNDLE_PURCHASE. */
export interface BundleBrandSelection {
  productId: string
  /** Cheapest variant matching this child's selection; `undefined` until the buy box resolves one. */
  minPriceVariantId?: number
}

/**
 * Bundle variant, ported from non-PL `BrandComparisonBundle`: quote each competitor at each child's
 * resolved variant (non-PL's `getBrandComparison` variant filter), sum per brand across children, keep
 * only brands that priced every child. Insertion order is the render order — non-PL never sorts.
 */
export function toBundleComparisonBrands(
  data: BrandComparisonWidgetData,
  selections: readonly BundleBrandSelection[],
): ProductComparisonBrand[] {
  const accumulated = new Map<string, { brand: ProductComparisonBrand; count: number }>()

  for (const { productId, minPriceVariantId } of selections) {
    const brandComparisons: ProtoBrandComparison[] = data.products[productId]?.brandComparisons ?? []
    for (const brand of brandComparisons) {
      if (!brand.showOnSite) continue
      const key = brand.brand ?? ''
      const previous = accumulated.get(key)

      // Unresolved child (no variant) yields no entry — non-PL bails the whole child the same way.
      const entry =
        minPriceVariantId === undefined ? undefined : brand.prices.find(price => price.variantId === minPriceVariantId)
      if (!entry || entry.price <= 0) {
        // Unpriced for this variant: keep without incrementing `count` so the final filter drops it.
        if (!previous) accumulated.set(key, { brand: toBrand(brand, []), count: 0 })
        continue
      }

      accumulated.set(key, {
        brand: toBrand(brand, [
          new ComparisonBrandPrice({
            // Running total across children; only these three fields, matching non-PL's accumulator.
            price: (previous?.brand.prices[0]?.price ?? 0) + entry.price,
            variantId: entry.variantId,
            updatedAt: entry.updatedAt ? new Date(entry.updatedAt) : undefined,
          }),
        ]),
        count: (previous?.count ?? 0) + 1,
      })
    }
  }

  const result: ProductComparisonBrand[] = []
  accumulated.forEach(({ brand, count }) => {
    if (count === selections.length) result.push(brand)
  })
  return result
}

/** Shared proto-brand → domain-brand mapping for both the single-product and bundle paths. */
function toBrand(brand: ProtoBrandComparison, prices: ComparisonBrandPrice[]): ProductComparisonBrand {
  return new ProductComparisonBrand({
    brand: brand.brand ?? '',
    brandDisplayName: brand.brandDisplayName ?? '',
    freeShipping: brand.freeShipping ?? false,
    freeReturns: brand.freeReturns ?? 0,
    showOnSite: brand.showOnSite ?? false,
    additionalProperties: mapAdditionalProperties(brand.additionalProperties),
    prices,
  })
}

/**
 * Wrap the widget's proto `Classification` in the minimal `Product` shape the DS comparison component
 * reads. `ProductPriceComparisonDS` derives `isProductDogFood` / `shouldShowPricePerUnit` from
 * `product.classification` only — but the PL widget carries just the proto classification, not a full
 * `Product` domain object. Conveying the classification is required for parity: passing `undefined`
 * would silently drop the dog-food / vitamin comparison rows the legacy PL path renders.
 *
 * The narrowing cast is safe because those two reads touch nothing else on `Product`.
 */
export function toComparisonProduct(classification: BrandComparisonWidgetData['classification']): Product | undefined {
  if (!classification) return undefined
  return { classification } as unknown as Product
}
